import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { useDebounce, useLocalStorage } from 'react-use';
import itemService from '../../services/ItemService';
import AlertDialog from '../../shared/dialog/AlertDialog';
import ItemTable from './table/ItemTable';
import { formattedItem } from './table/helper';

const actionCopy = {
  delete: {
    message: 'Are you sure you want to delete the selected items? They will be made inactive in QuickBooks and Verd.',
    success: 'Successfully deleted selected items'
  },
  active: {
    message: 'Are you sure you want to make the selected items active in QuickBooks?',
    success: 'Successfully made selected items active in QuickBooks'
  },
  disable: {
    message: 'Are you sure you want to disable the selected items in Verd?',
    success: 'Successfully disabled selected items'
  },
  enable: {
    message: 'Are you sure you want to enable the selected items in Verd?',
    success: 'Successfully enabled selected items'
  }
};

const errorMessage = (error, fallback) => {
  const qboMessage = error?.response?.data?.message?.Fault?.Error?.[0]?.Detail;
  const apiMessage = error?.response?.data?.message;
  return typeof apiMessage === 'string' ? apiMessage : qboMessage || error?.message || fallback;
};

const Item = () => {
  const [itemLoading, setItemLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selection, setSelection] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [pageSizes] = useState([100, 200, 300]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [columnFilters, setColumnFilters] = useLocalStorage('itemColumnFilters', {});
  const qboActiveFilter = columnFilters?.Active || [];
  const qboActive = qboActiveFilter.length === 1 ? qboActiveFilter[0] : null;

  useDebounce(
    () => {
      setDebouncedValue(searchValue.trim());
      setCurrentPage(0);
    },
    2000,
    [searchValue]
  );

  useEffect(() => {
    let active = true;

    const fetchItems = async () => {
      setItemLoading(true);
      try {
        const queryParams = {
          take: pageSize,
          skip: pageSize * currentPage,
          ...(debouncedValue && { queryString: debouncedValue }),
          ...(qboActive !== null && { active: qboActive })
        };
        // eslint-disable-next-line no-undef
        const [itemRefs, itemFromDB] = await Promise.all([
          itemService.getItemsFromQBO(queryParams),
          itemService.getItemsFromDatabase()
        ]);
        if (!active) return;

        const qboItems = itemRefs?.data?.data?.QueryResponse?.Item || [];
        const itemRecords = itemFromDB?.data?.data || [];
        // eslint-disable-next-line no-undef
        const itemRecordsById = new Map(itemRecords.map((item) => [String(item.Id), item]));
        const nextRows = formattedItem(qboItems).map((row) => {
          const databaseItem = itemRecordsById.get(String(row.Id));
          return databaseItem
            ? {
                ...row,
                itemStatus: databaseItem.itemStatus ? 'Enabled' : 'Disabled',
                itemImageUrl: databaseItem.itemImageUrl
              }
            : row;
        });

        setRows(nextRows);
        setTotalCount(Number(itemRefs?.data?.newTotalCount) || 0);
      } catch (error) {
        if (active) {
          setRows([]);
          setTotalCount(0);
          toast.error(errorMessage(error, 'Could not fetch items from QuickBooks'));
        }
      } finally {
        if (active) setItemLoading(false);
      }
    };

    fetchItems();
    return () => {
      active = false;
    };
  }, [currentPage, pageSize, debouncedValue, qboActive, refreshVersion]);

  const handleColumnFiltersChange = useCallback(
    (nextFilters) => {
      const previousActive = columnFilters?.Active || [];
      const nextActive = nextFilters?.Active || [];
      if (JSON.stringify(previousActive) !== JSON.stringify(nextActive)) {
        setCurrentPage(0);
        setSelection([]);
      }
      setColumnFilters(nextFilters);
    },
    [columnFilters, setColumnFilters]
  );

  const requestAction = useCallback((action, itemIds) => {
    if (!itemIds.length) {
      toast.error(`Please select an item to ${action}.`);
      return;
    }
    setPendingAction({ action, itemIds });
  }, []);

  const closeActionDialog = () => setPendingAction(null);

  const applyAction = async () => {
    if (!pendingAction || actionLoading) return;
    const { action, itemIds } = pendingAction;
    setActionLoading(true);
    try {
      if (action === 'delete') {
        const items = itemIds.map((itemId) => ({ Id: itemId }));
        const response = await itemService.deactivateItems(items);
        // eslint-disable-next-line no-undef
        const savedItems = new Map((response?.data?.data || []).filter(Boolean).map((item) => [String(item.Id), item]));
        setRows((current) =>
          current.map((row) => {
            const saved = savedItems.get(String(row.id));
            return saved ? { ...row, ...saved, id: saved.Id, Active: false, itemStatus: 'Disabled' } : row;
          })
        );
      } else if (action === 'active') {
        const items = itemIds.map((itemId) => ({ Id: itemId }));
        const response = await itemService.activateItems(items);
        // eslint-disable-next-line no-undef
        const savedItems = new Map((response?.data?.data || []).filter(Boolean).map((item) => [String(item.Id), item]));
        setRows((current) =>
          current.map((row) => {
            const saved = savedItems.get(String(row.id));
            return saved ? { ...row, ...saved, id: saved.Id, Active: true } : row;
          })
        );
      } else {
        const enabled = action === 'enable';
        const selectedItems = rows
          .filter((row) => itemIds.some((itemId) => String(itemId) === String(row.id)))
          .map((row) => ({ Id: row.Id || row.id, Name: row.Name }));
        const response = await itemService.updateItemStatus(selectedItems, enabled);
        // eslint-disable-next-line no-undef
        const updatedIds = new Set((response?.data?.item || []).map((item) => String(item.Id)));
        setRows((current) =>
          current.map((row) =>
            updatedIds.has(String(row.id)) ? { ...row, itemStatus: enabled ? 'Enabled' : 'Disabled' } : row
          )
        );
      }
      setSelection([]);
      toast.success(actionCopy[action].success);
      setPendingAction(null);
    } catch (error) {
      toast.error(errorMessage(error, `Could not ${action} selected items`));
    } finally {
      setActionLoading(false);
    }
  };

  const handleInputChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const refreshItems = useCallback(() => setRefreshVersion((current) => current + 1), []);

  const tableActions = useMemo(
    () => ({
      onDeleteItems: (ids = selection) => requestAction('delete', ids),
      onMakeActiveItems: () => requestAction('active', selection),
      onDisableItems: () => requestAction('disable', selection),
      onEnableItems: () => requestAction('enable', selection)
    }),
    [requestAction, selection]
  );

  return (
    <div>
      <Toaster
        position="bottom-center"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 3000,
          success: { style: { background: '#36c95e', color: '#fff' } },
          error: { style: { background: '#f5251d', color: '#fff' } }
        }}
      />

      <AlertDialog
        open={Boolean(pendingAction)}
        setOpen={(open) => !open && closeActionDialog()}
        handleStatusChange={applyAction}
        message={pendingAction ? actionCopy[pendingAction.action].message : ''}
        handleDialogClose={closeActionDialog}
      />

      <ItemTable
        rows={rows}
        setRows={setRows}
        selection={selection}
        setSelection={setSelection}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageSizes={pageSizes}
        totalCount={totalCount}
        itemLoading={itemLoading}
        actionLoading={actionLoading}
        searchValue={searchValue}
        handleInputChange={handleInputChange}
        columnFilters={columnFilters || {}}
        setColumnFilters={handleColumnFiltersChange}
        onRefreshItems={refreshItems}
        {...tableActions}
      />
    </div>
  );
};

export default Item;
